/**
 * iter440 — Smart Reconcile Wizard.
 *
 * 3-step modal flow that surveys the unlinked bank-statement pool,
 * generates a one-click bulk-link plan, and applies it after the
 * user reviews/edits per-group targets.
 *
 *   Step 1 (analyse)  → fetches grouped-unlinked + suggested rules,
 *                       builds a plan {group, suggested_target, confidence}
 *   Step 2 (review)   → renders the plan; user can edit/skip rows.
 *   Step 3 (apply)    → loops bulk-from-stmt-txns per accepted row,
 *                       progress bar + per-row status.
 *
 * Heuristic for the auto-target:
 *   • outgoing + key_type=='name'  → expense_category 'Other'
 *   • outgoing + key_type=='account' → expense_category 'Bank Fees'
 *   • outgoing + key_type=='signature' → expense_category guessed from
 *     keywords (VAT|Tax → 'VAT Settlement', Salary → 'Salary', Fuel →
 *     'Fuel', else 'Other').
 *   • incoming → skipped by default (user must confirm).
 *
 * The wizard is intentionally conservative: nothing is linked without
 * the user explicitly ticking the row, and skipped rows stay in the
 * unlinked pool so they can be processed individually later.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Loader2, Sparkles, Check, X, AlertTriangle, Save, FolderOpen, Trash2, SlidersHorizontal } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import api from '@/lib/api';
import { toast } from 'sonner';

const EXPENSE_CATEGORIES = [
  'Rent', 'Utilities', 'Maintenance', 'Cleaning', 'Salary', 'Travel', 'Fuel', 'Transport',
  'Bank Fees', 'VAT Settlement', 'Government', 'Subscription Fees', 'Marketing', 'Other',
];

function guessCategory(group) {
  const label = (group.display_label || '').toLowerCase();
  const sig = group.key_type === 'signature' ? label : '';
  if (/vat|tax/.test(sig)) return 'VAT Settlement';
  if (/salary|payroll/.test(sig)) return 'Salary';
  if (/fuel|petrol|diesel/.test(sig)) return 'Fuel';
  if (/transport|trip/.test(sig)) return 'Transport';
  if (/rent/.test(sig)) return 'Rent';
  if (/utilit|electric|water|stc|zain|mobily/.test(sig)) return 'Utilities';
  if (group.key_type === 'account') return 'Bank Fees';
  return 'Other';
}

function buildPlanRow(group, suppliers = []) {
  const isOutgoing = group.direction === 'outgoing';
  // iter440 — supplier auto-binding. If the group's display_label
  // contains a known supplier name (substring, case-insensitive,
  // ≥4 chars overlap), default the target to that supplier instead
  // of an expense category. Powered by the same heuristic as
  // iter423 Smart Suggest.
  let supplier = null;
  if (isOutgoing && suppliers.length > 0) {
    const labelNorm = (group.display_label || '').toUpperCase().replace(/[^A-Z0-9]+/g, '');
    for (const s of suppliers) {
      const sNorm = (s.name || '').toUpperCase().replace(/[^A-Z0-9]+/g, '');
      if (sNorm.length >= 4 && (labelNorm.includes(sNorm) || sNorm.includes(labelNorm))) {
        supplier = s;
        break;
      }
    }
  }
  return {
    group_id: group.group_id,
    group,
    accepted: isOutgoing,
    target_kind: supplier ? 'supplier' : 'expense_category',
    supplier_id: supplier?.id || '',
    supplier_label: supplier?.name || '',
    category: guessCategory(group),
    sub_category: '',
    status: 'pending',
    result: null,
  };
}

export default function SmartReconcileWizard({ open, onClose, onApplied }) {
  const [step, setStep] = useState('analyse');   // analyse | review | apply | done
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // iter441 — Templates state
  const [templates, setTemplates] = useState([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  // iter442b — Confidence slider for fuzzy matching (default 2, range 1-8).
  // Lower = more permissive ("match anything that vaguely resembles"),
  // higher = stricter ("only confident matches"). Slider is enabled only
  // once a template is loaded.
  const [minFuzzyScore, setMinFuzzyScore] = useState(2);
  const [lastMatchSummary, setLastMatchSummary] = useState(null); // {exact, fuzzy}

  // ── Step 1: analyse ──────────────────────────────────────────────
  const runAnalyse = async () => {
    setLoading(true);
    try {
      const [groupsRes, suppliersRes, tplRes] = await Promise.all([
        api.get('/banking/grouped-unlinked', { params: { days: 365, min_count: 3, direction: 'all' } }),
        api.get('/suppliers').catch(() => ({ data: [] })),
        api.get('/banking/reconcile-templates').catch(() => ({ data: { templates: [] } })),
      ]);
      const groups = groupsRes.data?.groups || [];
      const suppliers = (suppliersRes.data || []).map(s => ({ id: s.id, name: s.name || s.full_name || '' }));
      setPlan(groups.map(g => buildPlanRow(g, suppliers)));
      setTemplates(tplRes.data?.templates || []);
      setStep('review');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analyse failed');
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && step === 'analyse') runAnalyse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // iter441 — Apply a template to the current plan: overlays the
  // saved {payee_type, payee_id, category, sub_category} onto matching
  // groups (matched via {key_type, key_value, direction} signature),
  // ticks them all, and unticks groups that have no template entry.
  //
  // iter442 — Now also accepts fuzzy matches (semantic label overlap)
  // returned by the backend, surfacing them with a different badge so
  // the user can review before confirming.
  const loadTemplate = async (tplId, scoreOverride = null) => {
    if (!tplId) return;
    const score = scoreOverride != null ? scoreOverride : minFuzzyScore;
    setLoading(true);
    try {
      const r = await api.post(
        `/banking/reconcile-templates/${tplId}/apply-to-current`,
        null,
        { params: { min_fuzzy_score: score } },
      );
      const matches = r.data?.matches || [];
      const matchedByGroupId = Object.fromEntries(
        matches.map(m => [m.group_id, m])
      );
      setPlan(p => p.map(row => {
        const m = matchedByGroupId[row.group_id];
        if (m) {
          const t = m.saved_target;
          return {
            ...row,
            accepted: true,
            target_kind: t.payee_type || 'expense_category',
            supplier_id: t.payee_type !== 'expense_category' ? (t.payee_id || '') : '',
            supplier_label: row.supplier_label,
            category: t.category || row.category,
            sub_category: t.sub_category || '',
            match_kind: m.match_kind || 'exact',
            fuzzy_source_label: m.fuzzy_source_label || null,
            fuzzy_score: m.fuzzy_score || null,
          };
        }
        return { ...row, accepted: false, match_kind: undefined, fuzzy_score: undefined };
      }));
      setAppliedTemplateId(tplId);
      const exact = r.data?.exact_match_count || 0;
      const fuzzy = r.data?.fuzzy_match_count || 0;
      setLastMatchSummary({ exact, fuzzy });
      // Don't toast on every slider drag — only on initial load (no override).
      if (scoreOverride == null) {
        const ungrouped = (r.data?.unmatched_template_items || []).length;
        if (fuzzy > 0) {
          toast.success(`Loaded template — ${exact} exact + ${fuzzy} fuzzy match${fuzzy === 1 ? '' : 'es'}`);
        } else {
          toast.success(`Loaded template — ${exact} exact match${exact === 1 ? '' : 'es'}`);
        }
        if (ungrouped > 0) {
          toast.info(`${ungrouped} template item${ungrouped === 1 ? '' : 's'} not present in current pool`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load template failed');
    } finally {
      setLoading(false);
    }
  };

  // iter441 — Save current accepted rows as a template.
  const saveTemplate = async (name, description = '') => {
    const accepted = plan.filter(p => p.accepted);
    if (accepted.length === 0) {
      toast.error('Tick at least one group before saving as a template');
      return;
    }
    setSaving(true);
    try {
      const items = accepted.map(row => ({
        key_type: row.group.key_type,
        key_value: row.group.key_value,
        direction: row.group.direction,
        display_label: row.group.display_label,
        payee_type: row.target_kind,
        payee_id: row.target_kind !== 'expense_category' ? (row.supplier_id || null) : null,
        category: row.target_kind === 'expense_category' ? row.category : null,
        sub_category: row.target_kind === 'expense_category' ? (row.sub_category || null) : null,
      }));
      const r = await api.post('/banking/reconcile-templates', { name, description, items });
      setTemplates(t => [r.data, ...t]);
      setShowSaveDialog(false);
      toast.success(`Template "${name}" saved with ${items.length} item${items.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (tplId) => {
    try {
      await api.delete(`/banking/reconcile-templates/${tplId}`);
      setTemplates(t => t.filter(x => x.id !== tplId));
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ── Step 3: apply (iter440 — single bulk-reconcile call, parallel server-side) ──
  const runApply = async () => {
    const accepted = plan.filter(p => p.accepted);
    if (accepted.length === 0) {
      toast.error('Tick at least one group to link');
      return;
    }
    // Validate per-row state before firing.
    for (const r of accepted) {
      if (r.target_kind === 'supplier' && !r.supplier_id) {
        toast.error(`"${r.group.display_label}" is set to Supplier but no supplier picked`);
        return;
      }
    }
    setStep('apply');
    setProgress({ done: 0, total: accepted.length });
    setPlan(p => p.map(x => x.accepted ? { ...x, status: 'linking' } : x));

    const payloadItems = accepted.map(row => ({
      group_id: row.group_id,
      txn_refs: row.group.transactions.map(t => ({
        statement_id: t.statement_id, txn_index: t.txn_index,
      })),
      payee_type: row.target_kind,                // 'expense_category' | 'supplier'
      payee_id: row.target_kind === 'supplier' ? row.supplier_id : null,
      category: row.target_kind === 'expense_category' ? row.category : null,
      sub_category: row.target_kind === 'expense_category' ? (row.sub_category || '') : null,
    }));

    try {
      const r = await api.post('/banking/bulk-reconcile/apply-plan', {
        items: payloadItems,
        max_concurrency: 8,
      });
      const items = r.data?.items || [];
      // Stamp results back per row
      const byGid = Object.fromEntries(items.map(i => [i.group_id, i]));
      setPlan(p => p.map(x => {
        if (!x.accepted) return x;
        const res = byGid[x.group_id];
        if (!res) return { ...x, status: 'error', result: { error: 'no response' } };
        return {
          ...x,
          status: res.error ? 'error' : 'done',
          result: res.error
            ? { error: res.error }
            : { created: res.created_count, skipped: res.skipped_count },
        };
      }));
      setProgress({ done: accepted.length, total: accepted.length });
      const totalCreated = r.data?.total_created || 0;
      const totalSkipped = r.data?.total_skipped || 0;
      const elapsedMs = r.data?.elapsed_ms || 0;
      if (totalCreated > 0) toast.success(`Linked ${totalCreated} bank lines in ${(elapsedMs / 1000).toFixed(1)}s`);
      if (totalSkipped > 0) toast.info(`${totalSkipped} lines skipped (already linked)`);
      if (r.data?.error_items > 0) toast.error(`${r.data.error_items} group${r.data.error_items === 1 ? '' : 's'} failed`);
      // iter441 — Stamp last_applied_at on the template if one was used.
      if (appliedTemplateId) {
        api.post(`/banking/reconcile-templates/${appliedTemplateId}/touch-last-applied`,
          { applied_count: totalCreated }).catch(() => { /* non-blocking */ });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply-plan failed');
      setPlan(p => p.map(x => x.accepted && x.status === 'linking'
        ? { ...x, status: 'error', result: { error: e?.response?.data?.detail || e.message } } : x));
    }
    setStep('done');
    if (typeof onApplied === 'function') onApplied();
  };

  // ── Row helpers ──────────────────────────────────────────────────
  const updateRow = (gid, patch) =>
    setPlan(p => p.map(x => x.group_id === gid ? { ...x, ...patch } : x));

  const acceptedCount = plan.filter(p => p.accepted).length;
  const totalLines = plan.reduce((s, p) => s + (p.accepted ? p.group.count : 0), 0);

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && step !== 'apply' && onClose?.()}>
      <DialogContent className="sm:max-w-4xl max-h-[90dvh] overflow-y-auto" data-testid="smart-reconcile-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-violet-500" />
            Smart Reconcile Wizard
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            One-click bulk-link plan for unlinked bank-statement lines. Review the suggestions,
            untick what you don't want, then apply.
          </p>
        </DialogHeader>

        {step === 'analyse' && (
          <div className="p-12 text-center" data-testid="wiz-analysing">
            <Loader2 className="animate-spin inline mb-3" size={28} />
            <div className="text-sm">Analysing unlinked bank lines…</div>
          </div>
        )}

        {step === 'review' && (
          <ReviewStep plan={plan} updateRow={updateRow}
            acceptedCount={acceptedCount} totalLines={totalLines}
            templates={templates}
            loadTemplate={loadTemplate}
            deleteTemplate={deleteTemplate}
            onSaveClick={() => setShowSaveDialog(true)}
            appliedTemplateId={appliedTemplateId}
            minFuzzyScore={minFuzzyScore}
            setMinFuzzyScore={setMinFuzzyScore}
            lastMatchSummary={lastMatchSummary}
            loading={loading} />
        )}

        {(step === 'apply' || step === 'done') && (
          <ApplyStep plan={plan} progress={progress} done={step === 'done'} />
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div className="text-xs text-muted-foreground">
            {step === 'review' && plan.length > 0 &&
              `${acceptedCount} of ${plan.length} groups · ${totalLines} lines to link`}
          </div>
          <div className="flex gap-2">
            {step === 'review' && (
              <>
                <Button variant="outline" onClick={() => onClose?.()} data-testid="wiz-cancel">
                  Cancel
                </Button>
                <Button onClick={runApply}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  disabled={acceptedCount === 0}
                  data-testid="wiz-apply">
                  Apply plan ({acceptedCount} group{acceptedCount === 1 ? '' : 's'})
                </Button>
              </>
            )}
            {step === 'apply' && (
              <Button disabled className="bg-violet-600 text-white" data-testid="wiz-applying">
                <Loader2 size={12} className="animate-spin mr-1" /> Linking…
              </Button>
            )}
            {step === 'done' && (
              <Button onClick={() => onClose?.(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="wiz-done">
                Close
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* iter441 — Save-as-template sub-dialog */}
    {showSaveDialog && (
      <SaveTemplateDialog
        plan={plan.filter(p => p.accepted)}
        saving={saving}
        onCancel={() => setShowSaveDialog(false)}
        onSave={(name, desc) => saveTemplate(name, desc)}
      />
    )}
    </>
  );
}

function SaveTemplateDialog({ plan, saving, onCancel, onSave }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="wiz-save-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save size={16} /> Save reconcile plan as template
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Saves the <strong>{plan.length}</strong> ticked group{plan.length === 1 ? '' : 's'} (and their target categories/suppliers).
            Next month you can run this template to auto-apply the same mapping to fresh unlinked lines.
          </p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Template name</label>
            <Input value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Monthly close — Feb"
              autoFocus data-testid="wiz-save-name" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Description (optional)</label>
            <Input value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What does this plan cover?" data-testid="wiz-save-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave(name.trim(), description.trim())}
            disabled={!name.trim() || saving}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="wiz-save-confirm">
            {saving ? <><Loader2 size={12} className="animate-spin mr-1" />Saving…</> : 'Save template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── Review step ──────────────────────────────

function ReviewStep({ plan, updateRow, acceptedCount, totalLines,
    templates, loadTemplate, deleteTemplate, onSaveClick, appliedTemplateId,
    minFuzzyScore, setMinFuzzyScore, lastMatchSummary, loading }) {
  if (plan.length === 0) {
    return (
      <div className="p-12 text-center text-muted-foreground">
        <div className="text-sm">No grouped lines found — nothing to reconcile.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="wiz-review">
      {/* iter441 — Templates banner */}
      <div className="rounded-md p-2.5 bg-violet-50 border border-violet-200" data-testid="wiz-templates-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold text-violet-900 uppercase tracking-wide">
            <FolderOpen size={11} className="inline mr-1" /> Templates
          </span>
          {templates.length === 0 ? (
            <span className="text-xs text-muted-foreground italic">
              No saved templates yet — finish a plan and click "Save as template" to reuse it next month.
            </span>
          ) : (
            <>
              <Select value={appliedTemplateId || ''}
                onValueChange={v => loadTemplate(v)}
                disabled={loading}>
                <SelectTrigger className="h-7 text-xs w-60" data-testid="wiz-template-picker">
                  <SelectValue placeholder="Run last plan / load a template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} · {t.item_count} items
                      {t.last_applied_at ? ` · last used ${(t.last_applied_at || '').slice(0, 10)}` : ' · never used'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {appliedTemplateId && (
                <button type="button"
                  onClick={() => deleteTemplate(appliedTemplateId)}
                  className="text-xs text-error hover:underline inline-flex items-center gap-1"
                  data-testid="wiz-template-delete">
                  <Trash2 size={11} /> Delete
                </button>
              )}
            </>
          )}
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs"
            onClick={onSaveClick}
            disabled={acceptedCount === 0}
            data-testid="wiz-save-template-btn">
            <Save size={11} className="mr-1" /> Save as template
          </Button>
        </div>
        {/* iter442b — Fuzzy confidence slider. Re-runs the template
            apply-to-current call when the user releases the slider
            (onValueCommit) so we don't thrash the backend during drag. */}
        {appliedTemplateId && (
          <div className="mt-2 pt-2 border-t border-violet-200" data-testid="wiz-fuzzy-slider-row">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[11px] font-medium text-violet-900 inline-flex items-center gap-1">
                <SlidersHorizontal size={11} /> Fuzzy match strictness
              </span>
              <div className="flex-1 min-w-[180px] max-w-[320px]">
                <Slider
                  value={[minFuzzyScore]}
                  min={1}
                  max={8}
                  step={1}
                  onValueChange={(v) => setMinFuzzyScore(v[0])}
                  onValueCommit={(v) => loadTemplate(appliedTemplateId, v[0])}
                  disabled={loading}
                  data-testid="wiz-fuzzy-slider"
                />
              </div>
              <Badge variant="outline" className="text-[10px] font-mono"
                data-testid="wiz-fuzzy-score-value">
                ≥ {minFuzzyScore}
              </Badge>
              {lastMatchSummary && (
                <span className="text-[10px] text-muted-foreground">
                  → {lastMatchSummary.exact} exact + {lastMatchSummary.fuzzy} fuzzy
                </span>
              )}
              <span className="text-[10px] text-muted-foreground italic">
                {minFuzzyScore <= 2 ? 'permissive — catches loose matches' :
                  minFuzzyScore >= 5 ? 'strict — only high-confidence overlaps' :
                  'balanced'}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs p-2.5 rounded-md bg-violet-50 border border-violet-200">
        <Sparkles size={12} className="text-violet-600" />
        <span className="text-violet-900">
          Found <strong>{plan.length}</strong> grouped patterns covering <strong>{plan.reduce((s, p) => s + p.group.count, 0)}</strong> bank lines.
          Outgoing groups are pre-selected with a suggested category — incoming groups require explicit selection.
        </span>
      </div>
      <div className="border rounded-md max-h-[55dvh] overflow-y-auto divide-y">
        {plan.map(row => <PlanRow key={row.group_id} row={row} updateRow={updateRow} />)}
      </div>
    </div>
  );
}

function PlanRow({ row, updateRow }) {
  const g = row.group;
  const incoming = g.direction === 'incoming';
  return (
    <div className={`p-3 ${!row.accepted ? 'opacity-60' : ''}`} data-testid={`wiz-row-${g.group_id}`}>
      <div className="flex items-center gap-3 mb-2">
        <input type="checkbox" checked={row.accepted}
          onChange={e => updateRow(g.group_id, { accepted: e.target.checked })}
          className="h-4 w-4" data-testid={`wiz-row-accept-${g.group_id}`} />
        <Badge variant="outline" className="text-[10px] capitalize">{g.key_type}</Badge>
        {row.match_kind === 'fuzzy' && (
          <Badge className="bg-amber-100 text-amber-800 text-[10px] border-amber-300"
            title={`Fuzzy match — semantic overlap with template item "${row.fuzzy_source_label || ''}"`}
            data-testid={`wiz-row-fuzzy-${g.group_id}`}>
            ~ fuzzy
          </Badge>
        )}
        {row.match_kind === 'exact' && (
          <Badge className="bg-emerald-100 text-emerald-800 text-[10px] border-emerald-300"
            title="Exact template signature match">
            ✓ exact
          </Badge>
        )}
        <span className="flex-1 font-medium truncate">{g.display_label}</span>
        {row.target_kind === 'supplier' && row.supplier_label && (
          <Badge className="bg-violet-100 text-violet-800 text-[10px]">→ {row.supplier_label}</Badge>
        )}
        <Badge className={incoming ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}>
          {g.direction}
        </Badge>
        <Badge className="bg-amber-100 text-amber-800">{g.count} lines</Badge>
        <span className={`font-bold text-sm ${incoming ? 'text-success' : 'text-error'}`}>
          SR {g.total_amount.toLocaleString()}
        </span>
      </div>
      {row.accepted && (
        <div className="grid grid-cols-12 gap-2 pl-7">
          <div className="col-span-3">
            <label className="text-[10px] text-muted-foreground mb-0.5 block uppercase">Link as</label>
            <Select value={row.target_kind}
              onValueChange={v => updateRow(g.group_id, { target_kind: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`wiz-target-${g.group_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expense_category">Expense category</SelectItem>
                <SelectItem value="supplier">Supplier</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {row.target_kind === 'expense_category' ? (
            <>
              <div className="col-span-4">
                <label className="text-[10px] text-muted-foreground mb-0.5 block uppercase">Category</label>
                <Select value={row.category} onValueChange={v => updateRow(g.group_id, { category: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`wiz-cat-${g.group_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-5">
                <label className="text-[10px] text-muted-foreground mb-0.5 block uppercase">Sub-category (optional)</label>
                <Input value={row.sub_category}
                  onChange={e => updateRow(g.group_id, { sub_category: e.target.value })}
                  placeholder={`e.g. ${g.display_label}`} className="h-8 text-xs"
                  data-testid={`wiz-sub-${g.group_id}`} />
              </div>
            </>
          ) : (
            <div className="col-span-9">
              <label className="text-[10px] text-muted-foreground mb-0.5 block uppercase">Supplier</label>
              <div className="flex items-center h-8 px-2 text-xs border rounded-md bg-muted/30"
                data-testid={`wiz-supplier-${g.group_id}`}>
                {row.supplier_label || <span className="text-muted-foreground italic">No supplier auto-matched — switch to Expense category, or pick a supplier on the per-group dialog</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── Apply / Done step ────────────────────────

function ApplyStep({ plan, progress, done }) {
  const accepted = plan.filter(p => p.accepted);
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const totalCreated = plan.reduce((s, p) => s + (p.result?.created || 0), 0);
  const totalSkipped = plan.reduce((s, p) => s + (p.result?.skipped || 0), 0);
  const errors = plan.filter(p => p.status === 'error').length;
  return (
    <div className="space-y-3" data-testid="wiz-apply-step">
      <div className="rounded-md p-3 bg-muted/30 border">
        <div className="flex items-center justify-between mb-2 text-xs">
          <span>{done ? 'Done!' : `Linking… ${progress.done} of ${progress.total}`}</span>
          <span className="font-mono">{pct}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {done && (
        <div className="rounded-md p-3 bg-success/10 border border-success/30 text-xs space-y-0.5"
          data-testid="wiz-summary">
          <div><strong>{totalCreated}</strong> bank lines linked as expenses</div>
          {totalSkipped > 0 && <div className="text-muted-foreground">{totalSkipped} already linked, skipped</div>}
          {errors > 0 && <div className="text-error">{errors} group{errors === 1 ? '' : 's'} failed — see list below</div>}
        </div>
      )}
      <div className="border rounded-md max-h-[50dvh] overflow-y-auto divide-y">
        {accepted.map(row => (
          <div key={row.group_id} className="p-2 flex items-center gap-2 text-xs">
            {row.status === 'linking' && <Loader2 size={12} className="animate-spin text-info" />}
            {row.status === 'done' && <Check size={12} className="text-success" />}
            {row.status === 'error' && <X size={12} className="text-error" />}
            {row.status === 'pending' && <span className="w-3 h-3 rounded-full bg-muted" />}
            <span className="flex-1 truncate">{row.group.display_label}</span>
            <Badge variant="outline" className="text-[10px]">{row.category}</Badge>
            {row.result?.created != null && (
              <span className="text-success">+{row.result.created}</span>
            )}
            {row.result?.error && (
              <span className="text-error flex items-center gap-1">
                <AlertTriangle size={10} /> {String(row.result.error).slice(0, 30)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
